import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { Exclude, Expose } from "class-transformer";
import { IsNumber, IsOptional, IsPositive, Max, Min } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class DangerArea extends FloorplanItem {
  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceWidth" })
  private _referenceWidth?: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "_referenceHeight" })
  private _referenceHeight?: number;

  @IsNumber()
  @Min(-180)
  @Max(180)
  @Expose({ name: "rotation" })
  private _rotation: number;

  @Exclude()
  public sizeChanged: Subject<{ width: number; height: number }>;

  @Exclude()
  referenceSizeChanged: Subject<{ referenceWidth: number | undefined; referenceHeight: number | undefined }>;

  constructor(floorplan: Floorplan, id: string, x: number, y: number, width: number, height: number, rotation: number) {
    super(floorplan, id, x, y);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.sizeChanged = new Subject();
    this.referenceSizeChanged = new Subject();
    this.updateReferenceSize(this._width, this._height);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get referenceWidth(): number | undefined {
    return this._referenceWidth;
  }

  get referenceHeight(): number | undefined {
    return this._referenceHeight;
  }

  get rotation(): number {
    return this._rotation;
  }

  updateReferenceSize(width: number, height: number) {
    if (this.isReferenceMeasureSet() && width && height) {
      this._referenceWidth = Math.round((width / this._floorplan.getReferenceScale()) * 100) / 100;
      this._referenceHeight = Math.round((height / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceWidth = undefined;
      this._referenceHeight = undefined;
    }
  }

  updateSize(referenceWidth: number, referenceHeight: number) {
    this._width = referenceWidth * this._floorplan.getReferenceScale();
    this._height = referenceHeight * this._floorplan.getReferenceScale();
    this.updateForm(this._width, this._height, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.dangerAreasItemTextVisible;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const height = this.height;
    const rotation = this.rotation;
    const width = this.width;

    return (x: number, y: number) => DangerArea.create(this._floorplan, x, y, width, height, rotation);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.dangerAreas;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as DangerArea,
        this._floorplan.dangerAreas,
        FloorplanEventType.DANGER_AREA_ADDED,
        () => this._floorplan.project.setDangerAreaPositionIds(),
        afterDuplicateAdded as ((duplicate: DangerArea) => void) | undefined,
      );
  }

  override delete() {
    this.sizeChanged.unsubscribe();
    this._floorplan.deleteDangerArea(this);
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this.sizeChanged.next({ width, height });
    this.updateReferenceSize(width, height);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshDangerAreasLock();
  }

  static create(floorplan: Floorplan, x: number, y: number, width: number, height: number, rotation: number) {
    return new DangerArea(floorplan, uuidv4(), x, y, width, height, rotation);
  }
}
